<script setup>
import dayjs from 'dayjs';
import { isEqual, orderBy, uniq, uniqBy } from 'lodash';
import { onMounted } from 'vue';
import { useModal } from 'vue-final-modal';
import BiDatasetImportPopup from '~/bi/components/dataset-picker/dataset-picker-upload/bi-dataset-import-popup.vue';
import BiEditColumnPopup from '~/bi/components/dataset-picker/dataset-picker-upload/bi-edit-column-popup.vue';
import BiHandsontable from '~/bi/components/widgets/table-widget/bi-handsontable.vue';
import { useBiDataFormatter } from '~/bi/components/widgets/table-widget/composables/bi-table-data-formatter.composable.js';
import { useBiTableHelper } from '~/bi/components/widgets/table-widget/composables/bi-table-helper.composables.js';
import { CustomEditors } from '~/bi/components/widgets/table-widget/composables/custom-editors.js';
import { useBiStore } from '~/bi/store/bi.store';
import HawkDeletePopup from '~/common/components/organisms/hawk-delete-popup.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { validateName } from '~/common/composables/custom-validators.js';

const props = defineProps({
  name: {
    type: String,
    default: '',
  },
  columns: {
    type: Array,
    default: () => [],
  },
  data: {
    type: Array,
    default: () => [],
  },
  tableSchema: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(['close', 'published']);

const { $t, $services, $toast, $date } = useCommonImports();

const { parseDateTimeSegments } = CustomEditors();
const { getUserTimezone } = useBiDataFormatter();

const bi_store = useBiStore();

const bi_edit_column_popup = useModal({
  component: BiEditColumnPopup,
  attrs: {
    onClose() {
      bi_edit_column_popup.close();
    },
  },
});

const state = reactive({
  is_loading: true,
  table_instance: null,
  dataset_name: props.name,
  last_modified_at: null,
  columns: [],
  data: [],
  dataset_name: props.name,
  is_renaming: false,
  is_publishing: false,
});

const context_menu_items = computed(() => {
  return [
    {
      label: $t('Import'),
      uid: 'import',
      on_click: () => {
        const bi_dataset_import_popup = useModal({
          component: BiDatasetImportPopup,
          attrs: {
            columns: state.columns,
            onClose() {
              bi_dataset_import_popup.close();
            },
            onImport: async (payload) => {
              const hot = state.table_instance.hotInstance;
              state.columns = payload.columns;
              state.data = payload.data;
              updateColumns(hot);
              hot.updateData(state.data);
              bi_dataset_import_popup.close();
            },
          },
        });
        bi_dataset_import_popup.open();
      },
    },
    {
      label: $t('Export'),
      uid: 'export',
      on_click: async () => {
        try {
          const name = state.dataset_name.split('/').pop();

          const ExcelJS = await import('exceljs');
          const { saveAs } = await import('file-saver');

          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet(name);

          const data = state.table_instance.hotInstance.getData();
          const colHeaders = state.table_instance.hotInstance.getColHeader();

          const headerRow = worksheet.addRow(colHeaders);
          data.forEach((row) => {
            worksheet.addRow(row);
          });

          headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: '000000' } };
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'D9D9D9' }, // light gray background
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
              top: { style: 'thin', color: { argb: '999999' } },
              left: { style: 'thin', color: { argb: '999999' } },
              bottom: { style: 'thin', color: { argb: '999999' } },
              right: { style: 'thin', color: { argb: '999999' } },
            };
          });
          worksheet.columns = colHeaders.map(() => ({
            width: 25,
          }));
          worksheet.views = [
            { state: 'frozen', ySplit: 1 }, // freeze first row
          ];

          const buffer = await workbook.xlsx.writeBuffer();
          saveAs(new Blob([buffer]), `${name}.xlsx`);
        }
        catch (error) {
          logger.error(error);
        }
      },
    },
  ];
});

function getFormattedColumns() {
  const columns_copy = state.columns.map(column =>
    ({ ...column, options: column.options ? [...column.options] : undefined }),
  );

  return columns_copy.map((column, index) => {
    const column_details = { ...column };
    delete column_details.renderer;
    // For column data which have . inside the name
    column_details.data = function (row, value) {
      if (arguments.length === 1) {
        return row[column.data]; // getter
      }
      else {
        row[column.data] = value; // setter
      }
    };

    if (column_details.type === 'date') {
      column_details.renderer = 'datetimeRenderer';
      column_details.editor = 'datetime';
      column_details.data_type = 'date';
      column_details.validator = false;
    }
    else if (column_details.type === 'timestamp') {
      column_details.type = 'date';
      column_details.editor = 'datetime';
      column_details.renderer = 'datetimeRenderer';
      column_details.data_type = 'timestamp';
      column_details.validator = false;
    }
    else if (column_details.type === 'numeric') {
      column_details.numericFormat = {
        pattern: '0,0.00',
        culture: 'en-US',
      };
    }
    else if (column_details.type === 'dropdown') {
      if (column_details.allow_multi_select) {
        column_details.editor = 'multiselect';
        column_details.type = 'text';
        column_details.renderer = 'multiSelectRenderer';
        column_details.handleUpdateOptions = (options) => {
          state.columns[index].options = options;
        };
      }
      else {
        column_details.type = 'autocomplete';
        column_details.filter = true;
        column_details.strict = false;
        column_details.allowInvalid = true;
        column_details.visibleRows = 8;
        column_details.source = function (query, process) {
          // Get current options
          const options = column_details.options || [];
          const search = (query || '').toLowerCase();
          let results = [];

          if (search && !options.map(o => o.toLowerCase()).includes(search)) {
            results.push(`+ Add "${query}"`);
          }

          // Filter matching items
          results = results.concat(
            options.filter(opt =>
              opt.toLowerCase().includes(search),
            ),
          );

          process(results);
        };
        column_details.validator = function (value, callback) {
          if (value && value.startsWith('+ Add "')) {
            const match = value.match(/"([^"]+)"/);
            const new_value = match ? match[1] : value;

            // Add new value to options
            column_details.options.push(new_value);
            state.columns[index].options = column_details.options;

            // Replace cell value with actual entry
            this.instance.setDataAtCell(this.row, this.col, new_value);

            callback(true);
          }
          else {
            if (column_details.options.includes(value)) {
              callback(true);
            }
            else {
              // Add new value to options
              column_details.options.push(value);
              state.columns[index].options = column_details.options;
              callback(true);
            }
          }
        };
      }
    }
    return column_details;
  });
}

function updateColumns(hot) {
  const has_updated_data = state.columns.some(column => column.data !== column.title);
  if (has_updated_data) {
    const data = hot.getData();
    const new_data = [];
    data.forEach((row) => {
      const updated_row = {};
      state.columns.forEach((column, col_index) => {
        updated_row[column.title] = row[col_index];
      });
      new_data.push(updated_row);
    });
    state.columns = state.columns.map(column => ({ ...column, data: column.title }));
    hot.updateData(new_data);
  }
  state.columns = state.columns.map((column, index) => {
    return {
      ...column,
      order_index: index,
    };
  });
  hot?.updateSettings({ columns: getFormattedColumns() });
  hot?.render();
}

function handleColumnOperations(hot, key, selection) {
  const colIndex = selection[0].start.col;
  const column_details = state.columns[colIndex];
  if (key === 'remove-column') {
    const delete_popup = useModal({
      component: HawkDeletePopup,
      attrs: {
        header: 'Delete column',
        content: 'Are you sure you want to delete the column?',
        onClose() {
          delete_popup.close();
        },
        confirm() {
          state.columns.splice(colIndex, 1);
          updateColumns(hot);
          delete_popup.close();
        },
      },
    });
    delete_popup.open();
  }
  else {
    const default_options = [];
    const existing_columns = state.columns.map(column => column.data);
    if (key === 'edit-column') {
      existing_columns.splice(colIndex, 1);
      if (column_details.type === 'dropdown') {
        default_options.push(...column_details.options);
      }
      else {
        const unique_values = uniq(hot.getDataAtCol(colIndex)).filter(val => !!val?.length);
        default_options.push(...unique_values);
      }
    }
    bi_edit_column_popup.patchOptions({
      attrs: {
        columnDetails: key === 'edit-column' ? column_details : null,
        allowedTypes: (key === 'edit-column' ? column_details?.allowed_types : []) ?? [],
        defaultOptions: default_options,
        existingColumns: existing_columns,
        onSave: async (new_column_details) => {
          if (key === 'edit-column') {
            if (['date', 'timestamp'].includes(new_column_details.type) && state.columns[colIndex]?.type !== new_column_details.type) {
              if (new_column_details.type === 'timestamp') {
                new_column_details.width = Math.max(state.columns[colIndex]?.width || 200, 200);
              }
            }
            state.columns[colIndex] = {
              ...state.columns[colIndex],
              ...new_column_details,
            };
          }
          else if (key === 'insert-column-left') {
            state.columns.splice(colIndex, 0, new_column_details);
          }
          else if (key === 'insert-column-right') {
            state.columns.splice(colIndex + 1, 0, new_column_details);
          }
          updateColumns(hot);
          bi_edit_column_popup.close();
        },
      },
    });
    hot.deselectCell();
    bi_edit_column_popup.open();
  }
}

function handleColumnResize(columns_widths) {
  state.columns = state.columns.map((column) => {
    return {
      ...column,
      width: columns_widths[column.data],
    };
  });
}

function handleColumnMove(new_columns) {
  state.columns = new_columns;
}

function additionalTableSettings() {
  return {
    fillHandle: true,
    rowHeaders: true,
    filters: true,
    manualColumnMove: true,
    manualRowMove: true,
    dropdownMenu: {
      items: {
        // ✅ Custom Edit Column
        'edit-column': {
          key: 'edit-column',
          name: 'Edit column',
          callback(key, selection) {
            handleColumnOperations(this, key, selection);
          },
        },

        // ✅ Custom Insert Left
        'insert-column-left': {
          key: 'insert-column-left',
          name: 'Insert column left',
          callback(key, selection) {
            handleColumnOperations(this, key, selection);
          },
        },

        // ✅ Custom Insert Right
        'insert-column-right': {
          key: 'insert-column-right',
          name: 'Insert column right',
          callback(key, selection) {
            handleColumnOperations(this, key, selection);
          },
        },

        // ✅ Custom Remove Column
        'remove-column': {
          key: 'remove-column',
          name: 'Remove column',
          callback(key, selection) {
            handleColumnOperations(this, key, selection);
          },
        },

        '---------': {},

        // ✅ Keep Filters
        'filter_by_condition': { name: 'Filter by condition' },
        'filter_by_value': { name: 'Filter by value' },
        'filter_action_bar': { name: 'Apply filter' },

      },
    },
    contextMenu: ['row_above', 'row_below', 'remove_row'],
    viewportRowRenderingOffset: 80,
    viewportColumnRenderingOffset: 30,
  };
}

function getTableHeight() {
  return window.innerHeight - 110;
}

function formattedPayload() {
  const hot = state.table_instance?.hotInstance;
  const data = hot.getData();

  const data_type_map = {
    text: 'text',
    date: 'date',
    numeric: 'float',
    dropdown: 'text',
    checkbox: 'boolean',
    timestamp: 'timestamp',
  };

  let parsed_columns = state.columns.map((column) => {
    return {
      width: column.width,
      order_index: column.order_index,

      data: column.data,
      type: column.type,
      data_type: data_type_map[column.type],

      allowed_types: column.allowed_types,

      ...(column.type === 'dropdown'
        ? {
            allow_multi_select: column.allow_multi_select,
            options: column.options,
            data_type: column.allow_multi_select ? 'array' : 'text',
          }
        : {}),
    };
  });
  const options_has_map = {};
  const parsed_data = data.map((row) => {
    state.columns.forEach((column, index) => {
      if (column.type === 'dropdown') {
        // Format data to array for multi select dropdown
        if (column.allow_multi_select) {
          row[index] = (row[index] || '').split(', ').map(v => v.trim()).filter(v => v.length);
        }

        // Add new options to options_has_map
        if (!options_has_map[index]) {
          options_has_map[index] = new Set(parsed_columns[index]?.options || []);
        }
        if (row[index]) {
          if (Array.isArray(row[index])) {
            row[index].forEach(v => options_has_map[index].add(v));
          }
          else {
            options_has_map[index].add(row[index]);
          }
        }
      }

      if (column.type === 'date' || column.type === 'timestamp') {
        if (row[index]) {
          const { actual_date } = parseDateTimeSegments(row[index]);
          if (actual_date.isValid()) {
            if (column.type === 'date') {
              row[index] = actual_date.format('YYYY-MM-DD');
            }
            else {
              const user_tz = getUserTimezone();
              // Set the timezone without converting - treat as already in user's timezone
              row[index] = actual_date.tz(user_tz, true).toISOString();
            }
          }
        }
      }

      else if (column.type === 'checkbox') {
        row[index] = (row[index] === 'true' || row[index] === true);
      }
    });
    return row;
  });
  parsed_columns = parsed_columns.map((column) => {
    if (column.type === 'dropdown') {
      column.options = [...options_has_map[column.order_index]];
    }
    return column;
  });
  return {
    name: state.dataset_name,
    columns: parsed_columns,
    data: parsed_data,
  };
}

async function handlePublishDataset() {
  state.is_publishing = true;
  const payload = formattedPayload();
  try {
    if (props.tableSchema) {
      const response = await $services.data_warehouse.updateCustomDataset({
        dataset_id: props.tableSchema.name,
        body: payload,
      });
      if (response?.data?.table) {
        $toast({
          title: $t('Success'),
          text: 'Dataset updated successfully',
          type: 'success',
        });
      }
    }
    else {
      const response = await $services.data_warehouse.createCustomDataset({
        body: payload,
      });
      if (response?.data?.table) {
        $toast({
          title: $t('Success'),
          text: 'Dataset created successfully',
          type: 'success',
        });
      }
    }
    await bi_store.getSchemas();
    emit('published');
  }
  catch (error) {
    logger.log(error);
    $toast({
      title: $t('Error'),
      text: error?.message || $t('Something went wrong'),
      type: 'error',
    });
  }
  state.is_publishing = false;
}

onMounted(async () => {
  state.is_loading = true;
  let columns = [];
  if (props.tableSchema) {
    const { data: { custom_dataset } } = await $services.data_warehouse.getCustomDataset({
      dataset_id: props.tableSchema.name,
    });
    state.last_modified_at = custom_dataset.last_modified_at;
    columns = custom_dataset.columns;
    state.data = custom_dataset.data.map((row) => {
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].type === 'timestamp' && row[i]) {
          const user_tz = getUserTimezone();
          const date = dayjs(row[i]).tz(user_tz);
          if (date.isValid()) {
            row[columns[i].data] = date.format('DD/MM/YYYY hh:mm A');
          }
          else {
            row[columns[i].data] = row[i];
          }
        }
        else {
          row[columns[i].data] = row[i];
        }
      }
      return row;
    });
    state.dataset_name = custom_dataset.name;
  }
  else {
    columns = props.columns;
    state.data = props.data;
    state.dataset_name = props.name;
  }
  columns = columns.map((column, index) => {
    return {
      order_index: index,
      title: column.data,
      ...column,
      readOnly: false,
    };
  });
  columns.sort((a, b) => a.order_index - b.order_index);
  state.columns = columns;
  state.is_loading = false;
});
</script>

<template>
  <HawkModalContainer
    :options="{
      escToClose: false,
      focusTrap: false, // For handsontable filters input
    }"
    content_class="h-full w-full rounded-none"
  >
    <template v-if="!state.is_loading">
      <HawkModalHeader class="!p-4 border-b" hide_close @close="emit('close')">
        <template #header>
          <div class="flex justify-between items-center w-full">
            <div class="flex justify-start items-center">
              <HawkButton type="plain" icon class="mr-2.5" size="sm" @click="emit('close')">
                <IconHawkArrowLeft />
              </HawkButton>
              <div class="flex flex-col gap-2 min-w-[400px]">
                <BiColumnNameEditor
                  :is-field="false"
                  :hide-update-visibility="true"
                  :is-simple-input="true"
                  :field="{ key: state.dataset_name, label: state.dataset_name }"
                  :custom-validator="(value) => validateName(value, '/')"
                  @rename="state.dataset_name = $event"
                />
                <div v-if="state.last_modified_at" class="text-xs text-gray-500">
                  {{ $t('Last updated') }}: {{ $date(state.last_modified_at, 'Do MMMM YYYY hh:mm a') }}
                </div>
              </div>
            </div>
            <div class="flex justify-end items-center gap-x-4">
              <HawkMenu additional_trigger_classes="!ring-0" :items="context_menu_items">
                <template #trigger>
                  <HawkButton type="plain">
                    <IconHawkDotsVertical />
                  </HawkButton>
                </template>
              </HawkMenu>
              <HawkButton :loading="state.is_publishing" @click="handlePublishDataset">
                <IconHawkRocketTwo />
                {{ $t('Publish dataset') }}
              </HawkButton>
            </div>
          </div>
        </template>
      </HawkModalHeader>
      <HawkModalContent class="!p-4 !h-full !max-h-[calc(100vh-64px)]">
        <BiHandsontable
          v-if="!state.is_loading"
          bi-table-id="bi-handsontable-edit-table"
          :data="state.data"
          :columns="getFormattedColumns()"
          :height="getTableHeight()"
          :additional-table-settings="additionalTableSettings()"
          :row-headers="false"
          :can-edit="true"
          class="h-full border"
          @table-instance="state.table_instance = $event"
          @column-resize="handleColumnResize"
          @column-move="handleColumnMove"
        />
      </HawkModalContent>
    </template>
    <HawkLoader v-else />
  </HawkModalContainer>
</template>
